/**
 * ScoreService: Abstraction layer for high score storage.
 * Currently uses localStorage. Designed to be replaceable with
 * an external API implementation in the future.
 *
 * Gracefully handles localStorage being unavailable (private browsing,
 * full storage quota, security restrictions). Falls back to in-memory
 * storage so the game remains fully playable without persistence.
 */

export interface HighScoreEntry {
  initials: string;
  score: number;
  date: string;
}

export interface IScoreService {
  getHighScores(): HighScoreEntry[];
  addScore(initials: string, score: number): void;
  isHighScore(score: number): boolean;
}

const STORAGE_KEY = 'space-invaders-neon-highscores';
const MAX_SCORES = 10;

/**
 * Check if localStorage is available and working.
 * Some browsers (Safari private mode, restricted iframes) throw
 * when accessing localStorage or when calling setItem.
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

export class ScoreService implements IScoreService {
  private static instance: ScoreService;
  private scores: HighScoreEntry[] = [];
  private storageAvailable: boolean;

  private constructor() {
    this.storageAvailable = isLocalStorageAvailable();
    this.loadScores();
  }

  static getInstance(): ScoreService {
    if (!ScoreService.instance) {
      ScoreService.instance = new ScoreService();
    }
    return ScoreService.instance;
  }

  getHighScores(): HighScoreEntry[] {
    return [...this.scores];
  }

  addScore(initials: string, score: number): void {
    const entry: HighScoreEntry = {
      initials: initials.toUpperCase().substring(0, 3),
      score,
      date: new Date().toISOString(),
    };

    this.scores.push(entry);
    this.scores.sort((a, b) => b.score - a.score);
    this.scores = this.scores.slice(0, MAX_SCORES);
    this.saveScores();
  }

  isHighScore(score: number): boolean {
    if (score <= 0) return false;
    if (this.scores.length < MAX_SCORES) return true;
    return score > this.scores[this.scores.length - 1].score;
  }

  private loadScores(): void {
    if (!this.storageAvailable) {
      this.scores = [];
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate parsed data is an array of score entries
        if (Array.isArray(parsed)) {
          this.scores = (parsed as HighScoreEntry[]).filter(
            (entry) =>
              typeof entry === 'object' &&
              entry !== null &&
              typeof entry.initials === 'string' &&
              typeof entry.score === 'number'
          );
        } else {
          this.scores = [];
        }
      }
    } catch {
      // localStorage unavailable or corrupted data
      // Silently fallback to in-memory only
      this.scores = [];
    }
  }

  private saveScores(): void {
    if (!this.storageAvailable) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.scores));
    } catch {
      // localStorage unavailable or quota exceeded - silently fail
      // Scores remain in memory for the current session
      this.storageAvailable = false;
    }
  }
}
